import React from 'react';
import { ILimecraftPlayer } from '../types';
import { useLimecraftEvent } from './useLimecraftEvent';

export const useIsolatedLimecraft = (
    containerId: string,
    aspectRatio: number,
    controlBarHeight: number,
) => {
    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const [player, setPlayer] = React.useState<ILimecraftPlayer | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isError, setIsError] = React.useState(false);
    const [calculatedHeight, setCalculatedHeight] = React.useState<number>(0);

    React.useLayoutEffect(() => {
        const updateHeight = () => {
            if (iframeRef.current) {
                const width = iframeRef.current.clientWidth;
                setCalculatedHeight(width / aspectRatio + controlBarHeight);
            }
        };
        const observer = new ResizeObserver(updateHeight);
        if (iframeRef.current) observer.observe(iframeRef.current);
        updateHeight();
        return () => observer.disconnect();
    }, [aspectRatio, controlBarHeight]);

    useLimecraftEvent(player, 'lc:player:error', () => {
        setIsError(true);
        setIsLoading(false);
    });

    React.useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const iframeWin = iframe.contentWindow as Window;
        if (!iframeWin) return;

        iframe.srcdoc = `
            <!DOCTYPE html>
            <html>
                <head>
                    <style>                        
                        html, body { margin: 0; padding: 0; height: 100%; width: 100%; background: transparent; position: relative; }
                        #${containerId} { width: 100%; height: 100%; }
                        iframe { width: 100%; height: 100%; display: block; border: none; }
                    </style>
                </head>
                <body>
                    <div id="${containerId}"></div>
                    <script src="https://platform.limecraft.com/player/player-api.js"></script>
                </body>
            </html>
        `;

        const handleLoad = () => {
            iframeWin.onLimecraftPlayerAPIReady = () => {
                const lcPlayer = new iframeWin.Limecraft.Player({
                    selector: `#${containerId}`,
                    embedOptions: {},
                    onPlayerEvent: (instance, eventName, eventArgs) => {
                        if (instance._localListeners?.[eventName]) {
                            instance._localListeners[eventName].forEach(
                                (callback) => callback(eventArgs),
                            );
                        }
                    },
                    onPlayerReady: (instance: ILimecraftPlayer) => {
                        instance._localListeners = {};

                        const originalAddListener =
                            instance.addListener.bind(instance);
                        instance.addListener = (eventName, callback) => {
                            if (callback && instance._localListeners) {
                                if (!instance._localListeners[eventName])
                                    instance._localListeners[eventName] = [];
                                instance._localListeners[eventName].push(
                                    callback,
                                );
                            }
                            return originalAddListener(eventName);
                        };
                        instance.removeListener = (eventName, callback) => {
                            if (callback && instance._localListeners) {
                                if (instance._localListeners[eventName])
                                    delete instance._localListeners[eventName];
                            }
                        };

                        setPlayer(lcPlayer);
                    },
                    onMediaLoaded: () => {
                        setIsLoading(false);
                    },
                });
            };

            if (iframeWin.Limecraft) iframeWin.onLimecraftPlayerAPIReady();
        };

        iframe.addEventListener('load', handleLoad);

        return () => {
            iframe.removeEventListener('load', handleLoad);
            setIsLoading(true);
            setIsError(false);
            setPlayer(null);
            if (iframeWin) iframeWin.onLimecraftPlayerAPIReady = undefined;
            iframe.src = 'about:blank';
        };
    }, [containerId, controlBarHeight, aspectRatio]);

    return { player, iframeRef, calculatedHeight, isLoading, isError };
};
